import { VideoPathStatusTracker, formatVideoPathDebugLine, type VideoStreamingStatus } from '@playcastdotio/PeerCore';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';
import { sinkStreamHealthLine } from '../observability/streamHealthSink';

/** Transitions are rare, so they also go to the always-on stream-health log for field sessions. */
const defaultSink = (line: string): void => {
  sinkWebrtcDebugLine(line);
  sinkStreamHealthLine(line);
};

const POLL_MS = 1000;

export type VideoPathDebugLoggerOptions = {
  /** The host's actual per-viewer video path, i.e. `NativeVideoHost.getStatus()`. */
  statusSource: () => VideoStreamingStatus[];
  /** On-disk NDJSON sink; defaults to webrtc-debug.ndjson (debug-gated) plus the always-on stream-health.ndjson. */
  sink?: (line: string) => void;
  log?: (message: string, detail: Record<string, unknown>) => void;
  pollMs?: number;
};

/**
 * Logs every video-path transition a viewer goes through — Electron → native starting →
 * native/rtp or native/webcodecs, and any fallback with its reason — to the console and to
 * `C:\ProgramData\Playcast\logs\webrtc-debug.ndjson` as `[VideoPath.host]` lines.
 *
 * NativeVideoHost exposes state, not events, so this polls at 1 Hz and diffs; a replay of
 * the NDJSON then answers "was this session actually on the C++ WebCodecs stream, and if
 * not, why" without DevTools or the guest device.
 */
export class VideoPathDebugLogger {
  private readonly tracker = new VideoPathStatusTracker();
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly sink: (line: string) => void;
  private readonly log: (message: string, detail: Record<string, unknown>) => void;

  constructor(private readonly options: VideoPathDebugLoggerOptions) {
    this.sink = options.sink ?? defaultSink;
    this.log = options.log ?? ((message, detail) => console.log(message, detail));
  }

  start(): void {
    if (this.timer) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), this.options.pollMs ?? POLL_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Poll once; exposed so a caller can force a log line at a known moment (e.g. a settings change). */
  tick(): void {
    for (const status of this.tracker.update(this.options.statusSource())) {
      const line = formatVideoPathDebugLine(status);
      this.log('[VideoPath.host]', { peerId: status.viewerId, line, ...status });
      this.sink(JSON.stringify({ t: new Date().toISOString(), tag: '[VideoPath.host]', peerId: status.viewerId, line, ...status }));
    }
  }
}
